import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Gift } from 'lucide-react';
import GlitchText from './GlitchText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUDIO_FILES = {
  1: `${BACKEND_URL}/api/static/audio/track1.mp3`,
  2: `${BACKEND_URL}/api/static/audio/track2.mp3`,
  3: `${BACKEND_URL}/api/static/audio/track3.mp3`,
  4: `${BACKEND_URL}/api/static/audio/track4.mp3`,
  5: `${BACKEND_URL}/api/static/audio/track5.mp3`,
  6: null,
  7: null,
};

const tracks = [
  { id: 1, title: 'MYSTERY BOX I', artist: 'RHY' },
  { id: 2, title: 'MYSTERY BOX II', artist: 'RHY' },
  { id: 3, title: 'MYSTERY BOX III', artist: 'RHY' },
  { id: 4, title: 'MYSTERY BOX IV', artist: 'RHY' },
  { id: 5, title: 'MYSTERY BOX V', artist: 'RHY' },
  { id: 6, title: 'MYSTERY BOX VI', artist: 'RHY' },
  { id: 7, title: 'MYSTERY BOX VII', artist: 'RHY' },
];

const Confetti = ({ isActive, onComplete }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  useEffect(() => {
    if (!isActive) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = 320; canvas.height = 480;
    const colors = ['#f472b6', '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f87171'];
    particlesRef.current = [];
    for (let i = 0; i < 50; i++) {
      particlesRef.current.push({ x: canvas.width / 2, y: canvas.height / 2, vx: (Math.random() - 0.5) * 15, vy: (Math.random() - 0.5) * 15 - 5, size: Math.random() * 8 + 4, color: colors[Math.floor(Math.random() * colors.length)], rotation: Math.random() * 360, rotationSpeed: (Math.random() - 0.5) * 10, life: 1 });
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current = particlesRef.current.filter(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.3; p.rotation += p.rotationSpeed; p.life -= 0.015; p.vx *= 0.99; if (p.life <= 0) return false; ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rotation * Math.PI / 180); ctx.fillStyle = p.color; ctx.globalAlpha = p.life; ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6); ctx.restore(); return true; });
      if (particlesRef.current.length > 0) animationRef.current = requestAnimationFrame(animate);
      else onComplete && onComplete();
    };
    animate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isActive, onComplete]);
  if (!isActive) return null;
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-30" />;
};

// Slow Motion Pastel Lava - Melts outward smoothly
const LavaWaveEffect = ({ isPlaying, buttonPosition }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({
    waves: [],
    particles: [],
    expansion: 0,
    time: 0,
    targetExpansion: 0
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const state = stateRef.current;
    
    // Center point (where play button is)
    const centerX = width / 2;
    const centerY = height * 0.5;

    // Pastel rainbow colors - soft and dreamy
    const colors = [
      { r: 255, g: 182, b: 193 },  // Pastel pink
      { r: 255, g: 218, b: 185 },  // Peach
      { r: 255, g: 255, b: 186 },  // Pastel yellow
      { r: 186, g: 255, b: 201 },  // Mint green
      { r: 186, g: 225, b: 255 },  // Baby blue
      { r: 202, g: 186, b: 255 },  // Lavender
      { r: 255, g: 186, b: 255 },  // Soft magenta
      { r: 255, g: 200, b: 220 },  // Rose
    ];

    const animate = () => {
      // SLOW MOTION - halftime speed
      state.time += 0.008;
      
      // Very slow, smooth melting expansion
      state.targetExpansion = isPlaying ? 1 : 0;
      const speed = isPlaying ? 0.008 : 0.015; // Much slower expansion, gentle vacuum
      state.expansion += (state.targetExpansion - state.expansion) * speed;

      // Soft fade trail for dreamy effect
      ctx.fillStyle = `rgba(5, 5, 5, ${isPlaying ? 0.03 : 0.05})`;
      ctx.fillRect(0, 0, width, height);

      if (state.expansion > 0.005) {
        const maxRadius = Math.sqrt(width * width + height * height) * 0.9;
        const currentRadius = maxRadius * state.expansion;

        // Draw melting pastel rings - slow and smooth
        const numWaves = 12;
        for (let w = 0; w < numWaves; w++) {
          // Very slow wave movement
          const waveOffset = (state.time * 0.5 + w * 0.3) % 2.5;
          const waveRadius = currentRadius * (0.15 + waveOffset * 0.35) * state.expansion;
          
          if (waveRadius > 0 && waveRadius < maxRadius) {
            const colorIndex = w % colors.length;
            const nextColorIndex = (w + 1) % colors.length;
            // Slow color blend
            const colorBlend = (Math.sin(state.time * 0.8 + w * 0.5) + 1) / 2;
            
            const r = colors[colorIndex].r * (1 - colorBlend) + colors[nextColorIndex].r * colorBlend;
            const g = colors[colorIndex].g * (1 - colorBlend) + colors[nextColorIndex].g * colorBlend;
            const b = colors[colorIndex].b * (1 - colorBlend) + colors[nextColorIndex].b * colorBlend;

            // Draw organic melting shape
            ctx.beginPath();
            for (let angle = 0; angle < Math.PI * 2; angle += 0.03) {
              // Slow, organic wave motion like melting
              const wave1 = Math.sin(angle * 3 + state.time * 0.8) * 20 * state.expansion;
              const wave2 = Math.sin(angle * 2 - state.time * 0.5) * 15 * state.expansion;
              const wave3 = Math.cos(angle * 4 + state.time * 0.6) * 12 * state.expansion;
              const meltDrip = Math.sin(angle * 5 + state.time) * 8 * state.expansion;
              const r_offset = waveRadius + wave1 + wave2 + wave3 + meltDrip;
              
              const x = centerX + Math.cos(angle) * r_offset;
              const y = centerY + Math.sin(angle) * r_offset * 0.7;
              
              if (angle === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.closePath();
            
            // Soft, dreamy alpha
            const alpha = (1 - waveOffset / 2.5) * 0.4 * state.expansion;
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
            ctx.lineWidth = 4 + Math.sin(state.time * 1.5 + w) * 2;
            ctx.stroke();

            // Soft glow
            ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
            ctx.shadowBlur = 30;
            ctx.stroke();
            ctx.shadowBlur = 0;

            // Fill with very subtle color
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.15})`;
            ctx.fill();
          }
        }

        // Slow floating particles - like bubbles in lava
        if (isPlaying && Math.random() < 0.15 * state.expansion) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 0.3 + Math.random() * 0.5; // Very slow
          const colorIdx = Math.floor(Math.random() * colors.length);
          state.particles.push({
            x: centerX,
            y: centerY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed * 0.7,
            size: 4 + Math.random() * 8,
            color: colors[colorIdx],
            life: 1,
            maxLife: 1,
            wobble: Math.random() * Math.PI * 2,
            trail: []
          });
        }

        // Update and draw particles - slow motion
        state.particles = state.particles.filter(p => {
          // Slow wobble motion
          p.wobble += 0.02;
          
          if (isPlaying) {
            // Slow outward drift with wobble
            p.x += p.vx * state.expansion * 0.5;
            p.y += p.vy * state.expansion * 0.5;
            p.x += Math.sin(p.wobble) * 0.3;
            p.y += Math.cos(p.wobble * 0.7) * 0.2;
            p.life -= 0.003; // Slow fade
          } else {
            // Gentle vacuum
            const dx = centerX - p.x;
            const dy = centerY - p.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 3) {
              p.x += dx * 0.03;
              p.y += dy * 0.03;
            }
            p.life -= 0.01;
          }

          // Store trail
          p.trail.push({ x: p.x, y: p.y, life: p.life });
          if (p.trail.length > 20) p.trail.shift();

          if (p.life <= 0) return false;

          // Draw soft trail
          if (p.trail.length > 1) {
            ctx.beginPath();
            ctx.moveTo(p.trail[0].x, p.trail[0].y);
            for (let i = 1; i < p.trail.length; i++) {
              ctx.lineTo(p.trail[i].x, p.trail[i].y);
            }
            ctx.strokeStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.life * 0.2})`;
            ctx.lineWidth = p.size * 0.4;
            ctx.lineCap = 'round';
            ctx.stroke();
          }

          // Draw soft glowing particle
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 1.5);
          gradient.addColorStop(0, `rgba(255, 255, 255, ${p.life * 0.6})`);
          gradient.addColorStop(0.3, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.life * 0.5})`);
          gradient.addColorStop(1, `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, 0)`);
          
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();

          return true;
        });

        // Soft center glow
        const centerGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, currentRadius * 0.5);
        const glowColor = colors[Math.floor(state.time * 0.3) % colors.length];
        centerGlow.addColorStop(0, `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, ${0.15 * state.expansion})`);
        centerGlow.addColorStop(0.5, `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, ${0.05 * state.expansion})`);
        centerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = centerGlow;
        ctx.fillRect(0, 0, width, height);
      }

      // Limit particles
      if (state.particles.length > 60) {
        state.particles = state.particles.slice(-50);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isPlaying]);

  return (
    <canvas 
      ref={canvasRef} 
      width={320} 
      height={480} 
      className="absolute inset-0 w-full h-full pointer-events-none" 
      style={{ zIndex: 5 }} 
      data-testid="lava-wave-effect" 
    />
  );
};

const MusicPreviewCard = ({ track, isActive, onPlay, onPause, hasAudio }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  const handlePlay = () => {
    if (!isActive) { 
      setShowConfetti(true); 
      onPlay(track); 
    } else {
      onPause();
    }
  };

  return (
    <div className={`relative flex-shrink-0 w-[280px] md:w-[320px] h-[400px] md:h-[480px] rounded-2xl overflow-hidden border transition-all duration-500 ${isActive ? 'border-white/30 scale-105' : 'border-white/10 scale-100'}`} data-testid={`music-preview-${track.id}`}>
      {/* Background */}
      <div className="absolute inset-0 bg-[#050505]" />
      
      {/* Lava Wave Effect - covers entire card */}
      <LavaWaveEffect isPlaying={isActive} />
      
      {/* Confetti on play */}
      <Confetti isActive={showConfetti} onComplete={() => setShowConfetti(false)} />
      
      {/* Content */}
      <div className="relative z-20 h-full flex flex-col justify-between p-6">
        <div>
          <GlitchText className="font-mono text-xs tracking-[0.2em] uppercase text-white/40">Preview</GlitchText>
          <h3 className="mt-2"><GlitchText className="font-heading text-2xl font-bold text-white tracking-wider">{track.title}</GlitchText></h3>
          <GlitchText className="font-mono text-sm text-white/60 mt-1">{track.artist}</GlitchText>
          {!hasAudio && <GlitchText className="font-mono text-xs text-yellow-500/60 mt-2">Coming soon</GlitchText>}
        </div>
        
        {/* Play Button - centered, floating box style when paused */}
        <div className="flex-1 flex items-center justify-center">
          <button 
            onClick={handlePlay} 
            disabled={!hasAudio} 
            className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
              isActive 
                ? 'bg-white/20 backdrop-blur-sm text-white scale-100 shadow-lg shadow-white/20' 
                : hasAudio 
                  ? 'bg-white/5 backdrop-blur-sm text-white/60 hover:bg-white/10 hover:text-white hover:scale-105 border border-white/10 hover:border-white/20' 
                  : 'bg-white/5 text-white/30 cursor-not-allowed border border-white/5'
            }`} 
            data-testid={`play-btn-${track.id}`}
          >
            {/* Floating animation ring when paused */}
            {!isActive && hasAudio && (
              <div className="absolute inset-0 rounded-2xl border border-white/20 animate-pulse" />
            )}
            {isActive ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
          </button>
        </div>
      </div>
      
      {/* Now Playing indicator */}
      {isActive && (
        <div className="absolute top-4 right-4 flex items-center gap-2 z-30">
          <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          <GlitchText className="font-mono text-xs text-white/60">NOW PLAYING</GlitchText>
        </div>
      )}
    </div>
  );
};

const MusicPreviews = () => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = 1;
    audioRef.current.addEventListener('ended', () => { setIsPlaying(false); setCurrentTrack(null); });
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
  }, []);

  const fadeIn = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = 0;
    let vol = 0;
    fadeIntervalRef.current = setInterval(() => {
      vol += 0.05;
      if (vol >= 1) { vol = 1; clearInterval(fadeIntervalRef.current); }
      if (audioRef.current && !isMuted) audioRef.current.volume = vol;
    }, 25);
  }, [isMuted]);

  const fadeOut = useCallback((cb) => {
    if (!audioRef.current) { cb && cb(); return; }
    let vol = audioRef.current.volume;
    fadeIntervalRef.current = setInterval(() => {
      vol -= 0.05;
      if (vol <= 0) { vol = 0; clearInterval(fadeIntervalRef.current); cb && cb(); }
      if (audioRef.current) audioRef.current.volume = vol;
    }, 25);
  }, []);

  const stopCurrentAudio = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setIsPlaying(false);
    setCurrentTrack(null);
  }, []);

  const handlePlay = useCallback((track) => {
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }

    const audioUrl = AUDIO_FILES[track.id];
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.volume = 0;
      
      audioRef.current.play()
        .then(() => {
          setCurrentTrack(track);
          setIsPlaying(true);
          fadeIn();
          timeoutRef.current = setTimeout(() => { fadeOut(stopCurrentAudio); }, 59000);
        })
        .catch(e => {
          console.error('Audio error:', e);
          setIsPlaying(false);
          setCurrentTrack(null);
        });
    }
  }, [isMuted, fadeIn, fadeOut, stopCurrentAudio]);

  const handlePause = useCallback(() => {
    fadeOut(() => {
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTrack(null);
    });
  }, [fadeOut]);

  const toggleMute = () => {
    const m = !isMuted;
    setIsMuted(m);
    if (audioRef.current) audioRef.current.volume = m ? 0 : 1;
  };

  const scrollTo = (d) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: d === 'next' ? 340 : -340, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 md:py-24 overflow-hidden" data-testid="music-previews-section">
      <div className="px-6 md:px-12 lg:px-24 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <GlitchText className="font-accent text-xs tracking-[0.3em] uppercase text-white/40">New Music</GlitchText>
            <h2 className="mt-2">
              <GlitchText as="span" className="font-heading text-3xl md:text-4xl font-bold text-white tracking-wider">PREVIEW TRACKS</GlitchText>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={toggleMute} className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors" data-testid="mute-btn">
              {isMuted ? <VolumeX className="w-5 h-5 text-white/60" /> : <Volume2 className="w-5 h-5 text-white/60" />}
            </button>
            <button onClick={() => scrollTo('prev')} className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors" data-testid="scroll-prev-btn">
              <SkipBack className="w-5 h-5 text-white/60" />
            </button>
            <button onClick={() => scrollTo('next')} className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors" data-testid="scroll-next-btn">
              <SkipForward className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide px-6 md:px-12 lg:px-24 pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {tracks.map((track) => (
          <div key={track.id} className="snap-center">
            <MusicPreviewCard
              track={track}
              isActive={currentTrack?.id === track.id && isPlaying}
              onPlay={handlePlay}
              onPause={handlePause}
              hasAudio={!!AUDIO_FILES[track.id]}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-6 gap-2">
        {tracks.map((track) => (
          <div key={track.id} className={`w-2 h-2 rounded-full transition-all duration-300 ${currentTrack?.id === track.id ? 'bg-yellow-400 w-6' : 'bg-white/20'}`} />
        ))}
      </div>
    </section>
  );
};

export default MusicPreviews;
